import { z, zCalendarDate } from '@harvestiq/zod';
import {
  RmaLgmCommodityCodes,
  RmaLgmTypeCodes,
  type RmaLgmCommodityCode,
  type RmaLgmTypeCode,
} from '@harvestiq/constants';
import {
  zDecimalNullable,
  zDecimalRequired,
  zCoercedBoolean,
} from '@harvestiq/zod';

// Helper to check if a coverage is dairy
const isDairyCoverage = (
  commodityCode: RmaLgmCommodityCode,
  typeCode: RmaLgmTypeCode
) => {
  return (
    commodityCode === RmaLgmCommodityCodes.DAIRY_CATTLE &&
    typeCode === RmaLgmTypeCodes.NO_TYPE_SPECIFIED
  );
};

// request
export const lgmIndemnityRequestSchema = z.object({
  coverages: z
    .array(
      z
        .object({
          commodityCode: z.nativeEnum(RmaLgmCommodityCodes),
          typeCode: z.nativeEnum(RmaLgmTypeCodes),
          deductible: z.coerce.number(),
          salesEffectiveDate: zCalendarDate(),
          referenceId: z.string(),
          marketings: z.array(
            z.object({
              marketingDate: zCalendarDate(),
              numberOfHead: z.coerce.number().min(0),
            })
          ),
          isPre2026BeginningFarmerRancher: zCoercedBoolean().optional(),
          isVeteranFarmerRancher: zCoercedBoolean().optional(),
          beginningFarmerRancherCropYearCount: z.coerce
            .number()
            .int()
            .nullable()
            .optional(),
          liveCattleTargetCwtPerHead: z.coerce.number().optional(),
          feederCattleTargetCwtPerHead: z.coerce.number().optional(),
          cornTargetBuPerHead: z.coerce.number().optional(),
          cornEquivalentAmounts: z
            .array(z.coerce.number().nullable())
            .length(10)
            .optional(),
          soybeanMealEquivalentAmounts: z
            .array(z.coerce.number().nullable())
            .length(10)
            .optional(),
        })
        .superRefine((data, ctx) => {
          const isDairy = isDairyCoverage(data.commodityCode, data.typeCode);

          if (isDairy) {
            // For dairy coverages, cornEquivalentAmounts is required
            if (!data.cornEquivalentAmounts) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'cornEquivalentAmounts is required for dairy cattle coverages',
                path: ['cornEquivalentAmounts'],
              });
            } else {
              // Ensure at least one valid (non-null) number is present
              const hasValidNumber = data.cornEquivalentAmounts.some(
                (val) => val !== null && typeof val === 'number'
              );
              if (!hasValidNumber) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message:
                    'cornEquivalentAmounts must contain at least one valid number for dairy cattle coverages',
                  path: ['cornEquivalentAmounts'],
                });
              }
            }

            // For dairy coverages, soybeanMealEquivalentAmounts is required
            if (!data.soybeanMealEquivalentAmounts) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  'soybeanMealEquivalentAmounts is required for dairy cattle coverages',
                path: ['soybeanMealEquivalentAmounts'],
              });
            } else {
              // Ensure at least one valid (non-null) number is present
              const hasValidNumber = data.soybeanMealEquivalentAmounts.some(
                (val) => val !== null && typeof val === 'number'
              );
              if (!hasValidNumber) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  message:
                    'soybeanMealEquivalentAmounts must contain at least one valid number for dairy cattle coverages',
                  path: ['soybeanMealEquivalentAmounts'],
                });
              }
            }
          }
        })
    )
    .min(1, 'At least one coverage is required')
    .max(100, 'Maximum 100 coverages allowed per request'),
});

export type LgmIndemnityRequest = z.infer<typeof lgmIndemnityRequestSchema>;

// response
export const lgmIndemnityResponseSchema = z.object({
  data: z.array(
    z.object({
      referenceId: z.string().optional(),

      grossMarginGuaranteesPerHead: zDecimalRequired().array().optional(),
      grossMarginGuaranteesPerCwt: zDecimalRequired().array().optional(),
      grossMarginGuarantee: zDecimalRequired(),
      totalPremium: zDecimalRequired(),
      producerPremium: zDecimalRequired(),
      subsidy: zDecimalRequired(),

      projectedGrossMarginsPerHead: zDecimalNullable().array(),
      projectedGrossMarginsPerCwt: zDecimalNullable().array(),
      projectedGrossMarginTotals: zDecimalNullable().array(),
      coverageProjectedGrossMarginTotal: zDecimalNullable(),

      actualGrossMarginsTotals: zDecimalNullable().array(),
      actualGrossMarginsPerHead: zDecimalNullable().array(),
      actualGrossMarginsPerCwt: zDecimalNullable().array(),
      coverageActualGrossMarginTotal: zDecimalNullable(),

      coverageProjectedIndemnityPerCwt: zDecimalNullable(),
      coverageProjectedIndemnityPerHead: zDecimalNullable(),
      coverageProjectedIndemnityTotal: zDecimalRequired(),
      coverageActualIndemnityPerCwt: zDecimalNullable(),
      coverageActualIndemnityPerHead: zDecimalNullable(),
      coverageActualIndemnityTotal: zDecimalNullable(),
    })
  ),
});

export type LgmIndemnityResponse = z.infer<typeof lgmIndemnityResponseSchema>;
